import { FC } from 'react';
import Image from 'next/image';
import { Stack, Box, Typography, Container, Alert, useTheme, Paper, Card } from '@mui/material';
import { DefaultPageProps } from '@/types/types/page/pages';
import { IIntroIconData } from '@/types/types/page/homePage';
import { REGISTER_INTRO_BLOCK } from 'consts/phrases/registrationPhrases';
import { getThemeProps } from '@/components/layoutUtil';
import { addOpacityToThemeColor } from 'utils/styles/addOpacity';
import { config } from '@/config';
import { IntroIcons } from '../IntroIcons/IntroIcons';
import { RegistrationSteps } from '../RegistrationSteps/RegistrationSteps';
import { usePhrase } from '@/hooks/general/usePhrase/usePhrase';
import { useHomeQueryParams } from '@/hooks/general/useHomeQueryParams/useHomeQueryParams';
import { useNonMemberPersonalDetailsQuery } from '@/hooks/queries/wallet/walletQueries';
import { ExternalBannerLink } from '../ExternalBannerLink/ExternalBannerLink';

import styles from '@/styles/modules/Registration.module.css';

interface HomeProps extends DefaultPageProps {
    introIconsData: IIntroIconData[];
}

const M3GR_REDESIGN_TITLE =
    "<b>Join the World's Largest Panel</b><ul><li>Physicians</li><li>Healthcare Professionals</li><li>Patients and Caregivers</li></ul>";

export const Home: FC<HomeProps> = ({ dictionary, registrationTemplate, introIconsData }) => {
    const { t } = usePhrase();
    const theme = useTheme();

    const {
        homeLayout: {
            hasWelcomeText,
            hasBackgroundImage,
            pageTitle: { textAlign: titleTextAlign, position: titlePosition },
            picBlock: {
                visible: picBlockVisible,
                position: picBlockPosition,
                picWidth,
                picHeight,
                src: picSrc,
            },
            description: { visible: descriptionVisible, position: descriptionPosition },
            iconsBlock: { position: iconsBlockPosition },
            registrationForm: { fullwidthForm },
        },
    } = getThemeProps();

    const {
        surveyStatus,
        isStatusInTheList,
        isAbleToRegisterStatus,
        isRegisterForFree,
        nonMemberToken,
        messageIdToShow,
    } = useHomeQueryParams();

    const { data: nonMemberPersonalData } = useNonMemberPersonalDetailsQuery(nonMemberToken);
    const userAcountExists = nonMemberPersonalData && nonMemberPersonalData?.userId;

    const mainTitle =
        registrationTemplate?.template?.header?.headerPhraseId ||
        (config.featureFlags.m3grRedesign ? M3GR_REDESIGN_TITLE : REGISTER_INTRO_BLOCK.INTRO_TITLE);
    const mainDescription =
        registrationTemplate?.template?.header?.bodyPhraseId ||
        REGISTER_INTRO_BLOCK.INTRO_PARAGRAPH;

    const backgroundOverlay = registrationTemplate?.template?.header?.backgroundOverlay;
    const backgroundImage = registrationTemplate?.template?.header?.backgroundImage;
    const introImage = registrationTemplate?.template?.header?.introImage;

    const hasCenteredBackground = hasBackgroundImage === 'centerTop';

    const overlayColor = addOpacityToThemeColor(theme.palette.primary.main, 0.9);

    const templateBackgroundOverlay = (() => {
        if (!backgroundOverlay) {
            return '';
        }

        return hasCenteredBackground
            ? `linear-gradient(180deg, ${overlayColor} 20%, transparent 50%),`
            : `linear-gradient(${overlayColor}, ${overlayColor}),`;
    })();

    const templateBackground =
        hasBackgroundImage && backgroundImage
            ? `${templateBackgroundOverlay} url("${
                  config.filesBasePath + backgroundImage
              }") ${hasCenteredBackground ? '' : 'left top / cover no-repeat'}`
            : '';

    const isPicBlockLeftSide = picBlockPosition === 'left';
    const isPicBlockBottom = picBlockPosition === 'bottom';

    const isIconsBlockLeftSide = iconsBlockPosition === 'left';

    const isDescriptionAfterTitle = descriptionVisible && descriptionPosition === 'top';
    const isDescriptionAtTheEnd = descriptionVisible && descriptionPosition === 'bottom';
    const isDescriptionInPicBlock = descriptionVisible && descriptionPosition === 'picBlock';

    const introPic = introImage ? config.filesBasePath + introImage : picSrc;
    const isPicBlockVisible = picBlockVisible || introImage;

    const isIntroPicBlock = isPicBlockVisible && !isPicBlockLeftSide && !isPicBlockBottom;
    const isLeftSidePicBlock = isPicBlockVisible && isPicBlockLeftSide;

    const hasIntroPic = isIntroPicBlock && introPic;
    const hasPicInBottom = picBlockVisible && introPic && isPicBlockBottom;

    const Title = ({ testid }: { testid: string }) => (
        <Typography
            component="h1"
            className={`homePageTitle ${hasIntroPic ? 'homePageTitleFullwidth' : ''}`}
            variant="h1"
            data-testid={testid}
            sx={{
                fontWeight: 'bold',
                textAlign: titleTextAlign,

                maxWidth: {
                    md: fullwidthForm && titleTextAlign === 'left' ? '75%' : '100%',
                },
            }}
            {...(config.featureFlags.m3grRedesign &&
            !registrationTemplate?.template?.header?.headerPhraseId
                ? { dangerouslySetInnerHTML: { __html: mainTitle as string } }
                : { children: t(mainTitle as number) })}
        />
    );

    const Description = ({ testid }: { testid: string }) => (
        <>
            <Typography
                variant="subtitle1"
                sx={{ mt: 3 }}
                className="homePageDescription"
                data-testid={testid}
            >
                {t(mainDescription)}
            </Typography>
            <ExternalBannerLink />
        </>
    );

    const PicImage = () => {
        if (!introPic) return null;
        return (
            <Image
                width={picWidth ?? 390}
                height={picHeight ?? 280}
                src={introPic}
                data-testid="introPic"
                alt=""
            />
        );
    };

    const hasBackgroundInWrapper = hasBackgroundImage === 'fullwidth' || hasCenteredBackground;
    const hasBackgroundInCard = hasBackgroundImage === 'container';
    const hasLeftSideBackground = hasBackgroundImage === 'leftSide';

    const getWrapperStyles = () => {
        const baseStyles = { py: 4 };

        if (!hasBackgroundInWrapper) {
            return baseStyles;
        }

        return {
            ...baseStyles,
            ...(hasCenteredBackground
                ? { backgroundImage: templateBackground }
                : { background: templateBackground }),
        };
    };

    const wrapperStyles = getWrapperStyles();

    const cardStyles = hasBackgroundInCard
        ? {
              background: templateBackground,
          }
        : {};

    const leftSideBlockStyles = hasLeftSideBackground
        ? {
              background: templateBackground,
          }
        : {};

    const wrapperTestId = hasBackgroundInWrapper
        ? 'introRegistrationWrapperWithBg'
        : 'introRegistrationWrapper';
    const cardTestId = hasBackgroundInCard ? 'introRegistrationWithBg' : 'introRegistration';

    return (
        <>
            <Box
                className="introRegistrationWrapper"
                sx={wrapperStyles}
                data-testid={wrapperTestId}
            >
                <Container>
                    <Stack
                        direction="column"
                        sx={{
                            justifyContent: 'flex-start',
                        }}
                    >
                        {hasWelcomeText && (
                            <Typography className="homePageWelcomeText" variant="h2">
                                {t(REGISTER_INTRO_BLOCK.INTRO_WELCOME_TEXT)}
                            </Typography>
                        )}
                        <Card
                            className={`${styles.registration} introRegistration`}
                            style={cardStyles}
                            data-testid={cardTestId}
                        >
                            {isStatusInTheList && (
                                <Alert
                                    severity="error"
                                    sx={{
                                        mb: isAbleToRegisterStatus ? 1 : 3,
                                    }}
                                >
                                    {t(surveyStatus)}
                                </Alert>
                            )}
                            {messageIdToShow && (
                                <Alert
                                    severity="info"
                                    variant="standard"
                                    icon={false}
                                    sx={{ mb: 3 }}
                                >
                                    {t(messageIdToShow)}
                                </Alert>
                            )}
                            {isAbleToRegisterStatus && (
                                <Alert
                                    severity="info"
                                    variant="outlined"
                                    icon={false}
                                    sx={{ mb: 3 }}
                                >
                                    {t(REGISTER_INTRO_BLOCK.INTRO_SURVEY_COMPLETE_TEXT)}
                                </Alert>
                            )}
                            {nonMemberPersonalData && isRegisterForFree && !userAcountExists && (
                                <Alert severity="success" icon={false} sx={{ mb: 3 }}>
                                    {t(REGISTER_INTRO_BLOCK.INTRO_SURVEY_REGISTER_FOR_FREE_TEXT)}
                                </Alert>
                            )}
                            {userAcountExists && (
                                <Alert severity="success" icon={false} sx={{ mb: 3 }}>
                                    {t(REGISTER_INTRO_BLOCK.INTRO_SURVEY_ACCOUNT_RECORD_FOUND)}
                                </Alert>
                            )}
                            {titlePosition === 'top' && (
                                <Box sx={{ mb: 3 }}>
                                    <Title testid="home_top_title" />
                                </Box>
                            )}
                            <Stack
                                direction={{
                                    xs: 'column',
                                    lg: fullwidthForm ? 'column' : 'row',
                                }}
                                sx={{
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Box
                                    className={`introTextBlock ${
                                        hasIntroPic ? 'introTextBlockWithPic' : ''
                                    } ${styles.registrationText}`}
                                    style={leftSideBlockStyles}
                                    sx={{
                                        width: {
                                            xs: '100%',
                                            lg: fullwidthForm ? '100%' : '45%',
                                        },

                                        marginBottom: {
                                            xs: fullwidthForm ? '0' : '5%',
                                            lg: '0',
                                        },

                                        marginRight: { xs: '0', lg: '5%' },
                                    }}
                                >
                                    {hasIntroPic && (
                                        <Paper
                                            className="introPicBlock"
                                            data-testid="introPicBlock"
                                        >
                                            <PicImage />
                                        </Paper>
                                    )}
                                    {titlePosition === 'inline' && (
                                        <Title testid="home_inline_title" />
                                    )}
                                    {(isDescriptionAfterTitle || isIntroPicBlock) && (
                                        <Description testid="home_top_description" />
                                    )}
                                </Box>
                                <Stack
                                    className="registrationContainer"
                                    direction={
                                        isIconsBlockLeftSide || isPicBlockLeftSide
                                            ? { xs: 'column', md: 'row' }
                                            : 'row'
                                    }
                                    sx={{
                                        alignItems: 'stretch',
                                        justifyContent: 'space-between',

                                        width: {
                                            xs: '100%',
                                            lg: fullwidthForm ? '100%' : '50%',
                                        },

                                        marginTop: fullwidthForm ? 3 : 0,
                                    }}
                                >
                                    {isIconsBlockLeftSide && (
                                        <IntroIcons
                                            registrationTemplate={registrationTemplate}
                                            introIconsData={introIconsData}
                                        />
                                    )}
                                    {isLeftSidePicBlock && (
                                        <Paper
                                            className="introPicBlock"
                                            data-testid="introPicBlock"
                                        >
                                            <PicImage />
                                            {isDescriptionInPicBlock && (
                                                <Description testid="home_inpic_description" />
                                            )}
                                        </Paper>
                                    )}
                                    <Box
                                        sx={{
                                            width: {
                                                xs: '100%',
                                                lg:
                                                    isIconsBlockLeftSide || isPicBlockLeftSide
                                                        ? '70%'
                                                        : '100%',
                                            },
                                        }}
                                    >
                                        <RegistrationSteps
                                            dictionary={dictionary}
                                            nonMemberPersonalData={nonMemberPersonalData}
                                            registrationTemplate={registrationTemplate}
                                            paymentToken={nonMemberToken}
                                        />
                                    </Box>
                                </Stack>
                            </Stack>
                        </Card>
                    </Stack>
                </Container>
            </Box>
            {!isIconsBlockLeftSide && (
                <IntroIcons
                    registrationTemplate={registrationTemplate}
                    introIconsData={introIconsData}
                />
            )}
            {isDescriptionAtTheEnd && !isIntroPicBlock && (
                <Container sx={{ my: 4 }} data-testid="">
                    <Stack
                        direction={['column', 'column', 'row']}
                        sx={{
                            gap: 3,
                        }}
                    >
                        {hasPicInBottom && (
                            <Paper className="bottomPicBlock" data-testid="bottomPicBlock">
                                <PicImage />
                            </Paper>
                        )}
                        <Description testid="home_bottom_description" />
                    </Stack>
                </Container>
            )}
        </>
    );
};
